<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        :lazy="true"
        @hidden="close"
        :title="title"
        no-close-on-backdrop
        no-close-on-esc
        no-enforce-focus
    >
        <div
            v-show="
                action === 'print' || (action === 'email' && currentPage === 1)
            "
        >
            <b-form-group label-cols="3" label="Include:" label-for="include">
                <loader :margin="0" v-if="isLoadingFilter" />
                <div v-else class="checkbox-wrapper">
                    <label>
                        <input
                            type="checkbox"
                            v-model="includeAll"
                            @change="selectAllFilter"
                        />
                        Include All
                    </label>
                    <label
                        v-for="filter in filters"
                        :key="filter.id"
                        :class="{
                            'is-children': filter.type === 'child',
                            'is-lower-children': filter.type === 'lower-child'
                        }"
                    >
                        <input
                            @change="event => selectItemFilter(event, filter)"
                            type="checkbox"
                            v-model="selectedFilters"
                            :value="filter.id"
                        />
                        {{ filter.label }}
                    </label>
                </div>
            </b-form-group>
        </div>

        <div v-show="action === 'email' && currentPage === 2">
            <b-form-group label-cols="3" label="To:" label-for="to">
                <recipient-email
                    v-model="recipients"
                    name="recipients"
                    multiple
                    placeholder="Client or Contact Email (required)"
                    @input="fetchTemplate"
                    :key="recipientComponentKey"
                    :client="clientDetails"
                    v-validate="'required'"
                    :class="{
                        'border-danger': formError.includes('recipients')
                    }"
                    :isContactClientSameKey="true"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Subject:" label-for="subject">
                <b-input
                    v-model="subject"
                    :disabled="isLoadingTemplate"
                    :placeholder="
                        isLoadingTemplate ? 'Loading...' : 'Subject (optional)'
                    "
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Message:" label-for="message">
                <tinymce
                    :init="tinymceConfig"
                    v-model="message"
                    @onKeyDown="tinymceKeydown"
                />

                <label class="cc">
                    <input type="checkbox" v-model="ccEmail" />
                    Send me a copy of this message
                </label>
            </b-form-group>
        </div>

        <template v-slot:modal-footer>
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading && action === 'print'"
                variant="primary"
                @click="submit(EXPORT_TYPE_PDF)"
                :disabled="selectedFilters.length <= 0"
            >
                Preview
            </b-button>

            <b-button
                v-if="!isLoading && emailPage1"
                variant="primary"
                @click="next"
                :disabled="selectedFilters.length <= 0"
            >
                Next
            </b-button>

            <b-button
                v-if="!isLoading && emailPage2"
                variant="primary"
                @click="prev"
            >
                Prev
            </b-button>
            <b-button
                v-if="!isLoading && emailPage2"
                variant="primary"
                @click="submit(EXPORT_TYPE_EMAIL)"
            >
                Email File
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import Loader from "@/module/commons/loader";
import RecipientEmail from "@/components/commons/recipients-email";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import PatientSoapService from "@/services/patient/patient-soap.js";
import ModalExport from "@/mixins/modal-export.js";

const EXPORT_TYPE_EMAIL = 1;
const EXPORT_TYPE_PDF = 3;

/**
 * @component modal-export
 * @description This component been used by patient profile export and soap profile export
 *              Both has different function for the list of files included, email content and submission
 *              If later needed to use from other module and might need to add another function, considered to triggered the function from the parent component instead of put everything here.
 */
export default {
    mixins: [ModalExport],
    components: {
        Loader,
        RecipientEmail,
        tinymce
    },
    props: {
        patientDetails: {
            type: Object
        },
        /**
         * Used from soap profile
         */
        soapId: {
            type: String
        },
        /**
         * Used from soap profile, use as veterinarian param
         * if null, then will render the API providers and set the first provider as the veterinarian params=
         */
        providerId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            EXPORT_TYPE_EMAIL,
            EXPORT_TYPE_PDF,
            title: "Export",
            action: "",
            isLoading: false,
            currentPage: 1, // print has only 1 page, email has 2 pages
            veterinarian: "",
            veterinarianOptions: [{ text: "Loading...", value: "" }],
            isLoadingVeterinarian: true,
            formError: [],
            isLoadingFilter: true,
            filters: [],
            selectedFilters: [],
            includeAll: false,
            recipients: [],
            recipientComponentKey: 0,
            clientDetails: null,
            subject: "",
            isLoadingTemplate: true,
            message: "",
            ccEmail: false,
            tinymceConfig: {
                ...tinymceHelper.core, // use spread operator to inject all vetter-related configs
                toolbar: false,
                plugins: tinymceHelper.plugins
            },
            isNextRequireFetch: true,
            isSubjectFirstLoaded: true,
            isSoap: true // To let modal-export.js know the mixin called from soap profile
        };
    },
    methods: {
        open(params = {}) {
            const { action } = params;

            if (action === "print") {
                this.title = "Export File";
            } else if (action === "email") {
                this.title = "Email File";
            }

            this.action = action;
            this.$refs.dialog.show();

            this.veterinarian = this.providerId;
            this.fetchFilter();
            if (!this.veterinarian) {
                this.fetchVeterinarians();
            }
        },
        next() {
            this.currentPage = 2; // no need to ++, because we have 2 pages only

            if (this.isNextRequireFetch) {
                this.setDefaultRecipient();
                this.fetchTemplate();
            }
        },
        async fetchFilter() {
            try {
                this.isLoadingFilter = true;

                const res = await PatientSoapService.postSoapExport(
                    this.soapId,
                    {
                        export_type:
                            this.action === "print"
                                ? EXPORT_TYPE_PDF
                                : EXPORT_TYPE_EMAIL,
                        dialog: true
                    }
                );

                if (res.resource) {
                    this.isLoadingFilter = false;
                    this.filters = this.properFilter(res.resource);
                } else {
                    throw new Error("Unable to fetch filter list.");
                }
            } catch (e) {
                this._message(e.message, "danger");
                this.isLoadingFilter = false;
            }
        },
        async submit(exportType) {
            let valid = true;
            if (exportType === EXPORT_TYPE_EMAIL) {
                valid = await this.$validator.validateAll();
            }

            if (valid) {
                try {
                    this.isLoading = true;
                    let elements = {};
                    let mrElements = {};

                    if (this.includeAll) {
                        elements["cb_include_all"] = "on";
                    }

                    for (const filterId of this.selectedFilters) {
                        const key = this.extractFilterKey(filterId);
                        if (filterId.includes("cb_mr")) {
                            mrElements[key] = "on";
                        } else {
                            elements[key] = "on";
                        }
                    }

                    let params = {
                        export_type: parseInt(exportType),
                        veterinarian: this.veterinarian,
                        elements: elements,
                        mr_elements: mrElements
                    };
                    let res = null;
                    if (exportType === EXPORT_TYPE_EMAIL) {
                        params.to = this.recipients.map(r => r.toIdArray);
                        params.subject = this.subject;
                        params.message = this.message;
                        params.timestamp = parseInt(this._now().toSeconds());
                        params.email_type = 12;
                        params.cc_email = this.ccEmail;

                        res = await PatientSoapService.postSoapSendEmail(
                            this.soapId,
                            params
                        );

                        if (res && res.resources && res.resources.state == 0) {
                            this._message("Record emailed");
                        } else {
                            throw new Error(res.resources.message);
                        }
                    } else {
                        res = await PatientSoapService.postSoapExport(
                            this.soapId,
                            params
                        );
                        if (res && res.resource) {
                            if (
                                exportType === EXPORT_TYPE_PDF &&
                                res.resource.path
                            ) {
                                this._message("Opening PDF");
                                window.open(res.resource.path);
                            }
                        } else {
                            throw new Error(res.data.message);
                        }
                    }

                    this.isLoading = false;
                    this.close();
                } catch (e) {
                    this.isLoading = false;
                    this._message(e.message, "danger");
                }
            }
        },
        extractFilterKey(filterKey) {
            var startIndex = filterKey.indexOf("[") + 1;
            var endIndex = filterKey.indexOf("]");
            return filterKey.substring(startIndex, endIndex);
        },
        async fetchTemplate() {
            try {
                this.isLoadingTemplate = true;

                const newValues = Array.from(
                    this.recipients
                        .reduce((map, item) => {
                            if (!map.has(item.oriId)) map.set(item.oriId, item);
                            return map;
                        }, new Map())
                        .values()
                );
                const res = await PatientSoapService.postSoapEmailContent(
                    this.soapId,
                    { recipients: newValues.map(r => r.toIdArray) }
                );

                if (res && res.resources) {
                    this.isLoadingTemplate = false;

                    if (this.isSubjectFirstLoaded) {
                        this.subject = res.resources.subject;
                        this.isSubjectFirstLoaded = false;
                    }
                    this.message = res.resources.message;
                    this.defaultMessage = res.resources.message;
                } else {
                    throw new Error("Unable to fetch email content.");
                }
            } catch (e) {
                this.isLoadingTemplate = false;
                this._message(e.message, "danger");
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import "@/scss/modal-export.scss";
</style>
